import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  IPlatformAdminRepository,
  PlatformAdminDashboard,
  PlatformAdminLandingUsage,
  PlatformAdminMonthlyRevenue,
  PlatformAdminModuleState,
  PlatformAdminTenant,
  PlatformAdminTenantViewAccess,
  PlatformAdminTenantViewHandoff,
  PlatformAdminTotals,
  UpdatePlatformTenantInput,
} from '../../../interfaces/src';

type RpcClient = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: SupabaseError | null }>;
};

type SupabaseError = {
  code?: string;
  details?: string;
  hint?: string;
  message: string;
};

type QueryClient = SupabaseClient & {
  from: (table: string) => {
    select: (columns?: string, options?: Record<string, unknown>) => QueryBuilder;
    update: (values: Record<string, unknown>) => QueryBuilder;
    upsert: (values: Record<string, unknown>, options?: Record<string, unknown>) => QueryBuilder;
  };
};

type QueryBuilder = {
  eq: (column: string, value: unknown) => QueryBuilder;
  order: (column: string, options?: Record<string, unknown>) => QueryBuilder;
  range: (from: number, to: number) => Promise<{ data: unknown; error: SupabaseError | null }>;
  then: Promise<{ data: unknown; error: SupabaseError | null }>['then'];
};

type Row = Record<string, unknown>;

const DIRECT_SELECT_LIMIT = 9999;

export class SupabasePlatformAdminRepository implements IPlatformAdminRepository {
  constructor(private supabase: SupabaseClient) {}

  async isAdmin(): Promise<boolean> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_is_admin',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) return this.isAdminFromExistingRpc();
      throw error;
    }
    return data === true;
  }

  async getDashboard(): Promise<PlatformAdminDashboard> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_dashboard',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) return this.getDashboardFromTables();
      throw error;
    }
    return this.withLandingUsage(await this.withUsage(mapDashboard(asRecord(data), true)));
  }

  async createTenantView(tenantId: string): Promise<PlatformAdminTenantViewHandoff> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_create_tenant_view',
      {
        p_tenant_id: tenantId,
      },
    );
    if (error) throw error;
    return mapTenantViewHandoff(asRecord(data));
  }

  async consumeTenantView(token: string): Promise<PlatformAdminTenantViewAccess> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_consume_tenant_view',
      {
        p_token: token,
      },
    );
    if (error) throw error;
    return mapTenantViewAccess(asRecord(data));
  }

  async updateTenant(tenantId: string, patch: UpdatePlatformTenantInput): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_update_tenant',
      {
        p_tenant_id: tenantId,
        p_patch: patch,
      },
    );
    if (error) {
      if (isMissingRpc(error)) return this.updateTenantFromTables(tenantId, patch);
      throw error;
    }
  }

  async setModule(tenantId: string, moduleKey: string, isEnabled: boolean): Promise<void> {
    const { error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_set_module',
      {
        p_tenant_id: tenantId,
        p_module_key: moduleKey,
        p_is_enabled: isEnabled,
      },
    );
    if (error) {
      if (isMissingRpc(error)) return this.setModuleFromTables(tenantId, moduleKey, isEnabled);
      throw error;
    }
  }

  private async isAdminFromExistingRpc(): Promise<boolean> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc('is_admin', {});
    if (error) throw error;
    return data === true;
  }

  private async getDashboardFromTables(): Promise<PlatformAdminDashboard> {
    const client = this.supabase as QueryClient;
    const tenants = await selectRows(client, 'tenants', [
      'id',
      'name',
      'subdomain',
      'custom_domain',
      'mf',
      'phone',
      'email',
      'owner_user_id',
      'plan',
      'is_active',
      'created_at',
      'updated_at',
    ].join(','));

    const [
      companySettings,
      storeSettings,
      subscriptions,
      members,
      modules,
      sales,
      purchases,
      clients,
      suppliers,
      articles,
      payments,
      storefrontProducts,
      onlineCustomers,
      abandonedCarts,
      proCustomers,
      proCommandes,
      notifications,
    ] = await Promise.all([
      selectRows(client, 'company_settings', 'tenant_id,company_name,logo_url,logo_small_url,primary_color'),
      selectRows(client, 'store_settings', 'tenant_id,store_name,default_language,currency,whatsapp_enabled,custom_domain'),
      selectRows(client, 'tenant_subscriptions', 'tenant_id,status,billing_cycle,trial_ends_at,current_period_end,canceled_at'),
      selectRows(client, 'tenant_members', 'tenant_id'),
      selectRows(client, 'module_settings', 'tenant_id,module_key,is_enabled'),
      selectRows(client, 'sales_documents', 'tenant_id,doc_type,channel,total_ttc,doc_date,created_at,updated_at'),
      selectRows(client, 'purchase_documents', 'tenant_id,doc_type,total_ttc,created_at,updated_at'),
      selectRows(client, 'clients', 'tenant_id'),
      selectRows(client, 'suppliers', 'tenant_id'),
      selectRows(client, 'articles', 'tenant_id'),
      selectRows(client, 'payments', 'tenant_id,amount,created_at'),
      selectRows(client, 'storefront_products', 'tenant_id,is_published,created_at,updated_at'),
      selectRows(client, 'online_customers', 'tenant_id,total_spent,last_order_at,created_at,updated_at'),
      selectRows(client, 'abandoned_carts', 'tenant_id,total_ttc,status,last_activity_at,created_at,updated_at'),
      selectRows(client, 'pro_customers', 'tenant_id,status,created_at,updated_at'),
      selectRows(client, 'pro_commandes', 'tenant_id,pttc,created_at,updated_at'),
      selectRows(client, 'admin_notifications', 'tenant_id,is_read,created_at'),
    ]);

    const companyByTenant = firstByTenant(companySettings);
    const storeByTenant = firstByTenant(storeSettings);
    const subscriptionByTenant = firstByTenant(subscriptions);
    const memberRows = groupByTenant(members);
    const moduleRows = groupByTenant(modules);
    const salesRows = groupByTenant(sales);
    const purchaseRows = groupByTenant(purchases);
    const clientRows = groupByTenant(clients);
    const supplierRows = groupByTenant(suppliers);
    const articleRows = groupByTenant(articles);
    const paymentRows = groupByTenant(payments);
    const storefrontRows = groupByTenant(storefrontProducts);
    const onlineRows = groupByTenant(onlineCustomers);
    const cartRows = groupByTenant(abandonedCarts);
    const proCustomerRows = groupByTenant(proCustomers);
    const proOrderRows = groupByTenant(proCommandes);
    const notificationRows = groupByTenant(notifications);

    const tenantModels = tenants.map((tenant) => {
      const tenantId = readString(tenant.id) ?? '';
      const tenantSales = salesRows.get(tenantId) ?? [];
      const tenantPurchases = purchaseRows.get(tenantId) ?? [];
      const tenantPayments = paymentRows.get(tenantId) ?? [];
      const tenantStorefront = storefrontRows.get(tenantId) ?? [];
      const tenantOnline = onlineRows.get(tenantId) ?? [];
      const tenantCarts = cartRows.get(tenantId) ?? [];
      const tenantPros = proCustomerRows.get(tenantId) ?? [];
      const tenantProOrders = proOrderRows.get(tenantId) ?? [];
      const tenantNotifications = notificationRows.get(tenantId) ?? [];
      const tenantModules = (moduleRows.get(tenantId) ?? []).map((row) => ({
        key: readString(row.module_key) ?? '',
        enabled: readBoolean(row.is_enabled),
      }));
      const company = companyByTenant.get(tenantId) ?? {};
      const store = storeByTenant.get(tenantId) ?? {};
      const subscription = subscriptionByTenant.get(tenantId) ?? {};

      const onlineOrderRows = tenantSales.filter((row) =>
        readString(row.doc_type) === 'order' && readString(row.channel) === 'storefront',
      );
      const invoiceRows = tenantSales.filter((row) => readString(row.doc_type) === 'invoice');
      const activeCarts = tenantCarts.filter((row) => readString(row.status) === 'active');

      return mapTenant({
        id: tenantId,
        name: readString(tenant.name) ?? '',
        subdomain: readString(tenant.subdomain) ?? '',
        customDomain: readString(tenant.custom_domain),
        mf: readString(tenant.mf),
        phone: readString(tenant.phone),
        email: readString(tenant.email),
        ownerUserId: readString(tenant.owner_user_id),
        ownerEmail: readString(tenant.email),
        plan: readString(tenant.plan) ?? 'free',
        isActive: readBoolean(tenant.is_active),
        createdAt: readString(tenant.created_at),
        updatedAt: readString(tenant.updated_at),
        companyName: readString(company.company_name),
        storeName: readString(store.store_name),
        logoUrl: readString(company.logo_small_url) ?? readString(company.logo_url),
        primaryColor: readString(company.primary_color),
        defaultLanguage: readString(store.default_language),
        currency: readString(store.currency),
        whatsappEnabled: readNullableBoolean(store.whatsapp_enabled),
        storeCustomDomain: readString(store.custom_domain),
        subscriptionStatus: readString(subscription.status),
        billingCycle: readString(subscription.billing_cycle),
        trialEndsAt: readString(subscription.trial_ends_at),
        currentPeriodEnd: readString(subscription.current_period_end),
        canceledAt: readString(subscription.canceled_at),
        teamMembers: (memberRows.get(tenantId) ?? []).length,
        enabledModules: tenantModules.filter((item) => item.enabled).length,
        modules: tenantModules,
        salesDocuments: tenantSales.length,
        salesOrders: tenantSales.filter((row) => readString(row.doc_type) === 'order').length,
        invoices: invoiceRows.length,
        invoiceRevenue: sumRows(invoiceRows, 'total_ttc'),
        onlineOrders: onlineOrderRows.length,
        onlineRevenue: sumRows(onlineOrderRows, 'total_ttc'),
        revenue30d: sumRows(tenantSales.filter((row) => isWithinDays(readString(row.doc_date), 30)), 'total_ttc'),
        purchaseDocuments: tenantPurchases.length,
        purchaseInvoiceTotal: sumRows(
          tenantPurchases.filter((row) => readString(row.doc_type) === 'invoice'),
          'total_ttc',
        ),
        clients: (clientRows.get(tenantId) ?? []).length,
        suppliers: (supplierRows.get(tenantId) ?? []).length,
        articles: (articleRows.get(tenantId) ?? []).length,
        payments: tenantPayments.length,
        paymentTotal: sumRows(tenantPayments, 'amount'),
        storefrontProducts: tenantStorefront.length,
        publishedProducts: tenantStorefront.filter((row) => readBoolean(row.is_published)).length,
        onlineCustomers: tenantOnline.length,
        onlineCustomerLtv: sumRows(tenantOnline, 'total_spent'),
        openAbandonedCarts: activeCarts.length,
        abandonedCartValue: sumRows(activeCarts, 'total_ttc'),
        proCustomers: tenantPros.length,
        pendingProCustomers: tenantPros.filter((row) => readString(row.status) === 'pending').length,
        approvedProCustomers: tenantPros.filter((row) => readString(row.status) === 'approved').length,
        proOrders: tenantProOrders.length,
        proOrderTotal: sumRows(tenantProOrders, 'pttc'),
        unreadNotifications: tenantNotifications.filter((row) => !readBoolean(row.is_read)).length,
        lastActivityAt: latestDate([
          ...tenantSales.map((row) => readString(row.updated_at) ?? readString(row.created_at)),
          ...tenantPurchases.map((row) => readString(row.updated_at) ?? readString(row.created_at)),
          ...tenantPayments.map((row) => readString(row.created_at)),
          ...tenantStorefront.map((row) => readString(row.updated_at) ?? readString(row.created_at)),
          ...tenantOnline.map((row) => readString(row.updated_at) ?? readString(row.last_order_at) ?? readString(row.created_at)),
          ...tenantCarts.map((row) => readString(row.last_activity_at) ?? readString(row.updated_at) ?? readString(row.created_at)),
          ...tenantPros.map((row) => readString(row.updated_at) ?? readString(row.created_at)),
          ...tenantProOrders.map((row) => readString(row.updated_at) ?? readString(row.created_at)),
          readString(tenant.updated_at),
        ]),
      });
    }).sort((a, b) => {
      const aTime = Date.parse(a.lastActivityAt ?? a.updatedAt ?? a.createdAt);
      const bTime = Date.parse(b.lastActivityAt ?? b.updatedAt ?? b.createdAt);
      return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
    });

    return this.withLandingUsage(await this.withUsage({
      generatedAt: new Date().toISOString(),
      features: {
        adminRpc: false,
        subscriptionStatusWrite: false,
      },
      totals: buildTotals(tenantModels),
      tenants: tenantModels,
      monthlyRevenue: buildMonthlyRevenue(sales),
    }));
  }

  private async withUsage(dashboard: PlatformAdminDashboard): Promise<PlatformAdminDashboard> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_usage',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) return dashboard;
      throw error;
    }

    const usageByTenant = new Map(
      readArray(data).map((item) => {
        const usage = mapTenantUsage(asRecord(item));
        return [usage.tenantId, usage] as const;
      }),
    );
    const tenants = dashboard.tenants.map((tenant) => {
      const usage = usageByTenant.get(tenant.id);
      if (!usage) return tenant;
      return {
        ...tenant,
        storageBytes: usage.storageBytes,
        storageFiles: usage.storageFiles,
        storageBuckets: usage.storageBuckets,
        storefrontVisits: usage.storefrontVisits,
        storefrontUniqueVisitors: usage.storefrontUniqueVisitors,
        storefrontVisits30d: usage.storefrontVisits30d,
        storefrontUniqueVisitors30d: usage.storefrontUniqueVisitors30d,
      };
    });

    return {
      ...dashboard,
      tenants,
      totals: buildTotals(tenants),
    };
  }

  private async withLandingUsage(dashboard: PlatformAdminDashboard): Promise<PlatformAdminDashboard> {
    const { data, error } = await (this.supabase as unknown as RpcClient).rpc(
      'platform_admin_landing_usage',
      {},
    );
    if (error) {
      if (isMissingRpc(error)) return dashboard;
      throw error;
    }

    return {
      ...dashboard,
      landing: mapLandingUsage(asRecord(data)),
    };
  }

  private async updateTenantFromTables(
    tenantId: string,
    patch: UpdatePlatformTenantInput,
  ): Promise<void> {
    const client = this.supabase as QueryClient;
    const tenantPatch: Row = {};
    if (patch.name !== undefined) tenantPatch.name = nullIfBlank(patch.name);
    if (patch.subdomain !== undefined) tenantPatch.subdomain = nullIfBlank(patch.subdomain.toLowerCase());
    if (patch.customDomain !== undefined) tenantPatch.custom_domain = nullIfBlank(patch.customDomain?.toLowerCase() ?? null);
    if (patch.mf !== undefined) tenantPatch.mf = nullIfBlank(patch.mf);
    if (patch.phone !== undefined) tenantPatch.phone = nullIfBlank(patch.phone);
    if (patch.email !== undefined) tenantPatch.email = nullIfBlank(patch.email?.toLowerCase() ?? null);
    if (patch.plan !== undefined) tenantPatch.plan = patch.plan;
    if (patch.isActive !== undefined) tenantPatch.is_active = patch.isActive;
    tenantPatch.updated_at = new Date().toISOString();

    if (Object.keys(tenantPatch).length > 1) {
      const { error } = await client.from('tenants').update(tenantPatch).eq('id', tenantId);
      if (error) throw error;
    }

    const storePatch: Row = {};
    if (patch.subdomain !== undefined) storePatch.subdomain = nullIfBlank(patch.subdomain.toLowerCase());
    if (patch.customDomain !== undefined) storePatch.custom_domain = nullIfBlank(patch.customDomain?.toLowerCase() ?? null);
    if (Object.keys(storePatch).length > 0) {
      storePatch.updated_at = new Date().toISOString();
      const { error } = await client.from('store_settings').update(storePatch).eq('tenant_id', tenantId);
      if (error) throw error;
    }

    const subscriptionPatch: Row = {};
    if (patch.subscriptionStatus !== undefined) subscriptionPatch.status = patch.subscriptionStatus;
    if (patch.billingCycle !== undefined) subscriptionPatch.billing_cycle = patch.billingCycle;
    if (patch.trialEndsAt !== undefined) subscriptionPatch.trial_ends_at = patch.trialEndsAt;
    if (patch.currentPeriodEnd !== undefined) {
      subscriptionPatch.current_period_end = patch.currentPeriodEnd;
    }
    if (patch.canceledAt !== undefined) subscriptionPatch.canceled_at = patch.canceledAt;

    if (Object.keys(subscriptionPatch).length > 0) {
      const { error } = await client.from('tenant_subscriptions').upsert(
        {
          tenant_id: tenantId,
          ...subscriptionPatch,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'tenant_id' },
      );
      if (error) {
        if (isWriteDenied(error)) {
          throw new Error('Subscription status updates require the platform admin database migration.');
        }
        throw error;
      }
    }
  }

  private async setModuleFromTables(
    tenantId: string,
    moduleKey: string,
    isEnabled: boolean,
  ): Promise<void> {
    const { error } = await (this.supabase as QueryClient).from('module_settings').upsert(
      {
        tenant_id: tenantId,
        module_key: moduleKey,
        is_enabled: isEnabled,
      },
      { onConflict: 'tenant_id,module_key' },
    );
    if (error) throw error;
  }
}

async function selectRows(
  client: QueryClient,
  table: string,
  columns: string,
): Promise<Row[]> {
  const { data, error } = await client
    .from(table)
    .select(columns)
    .range(0, DIRECT_SELECT_LIMIT);
  if (error) {
    if (table === 'tenants') throw error;
    return [];
  }
  return readArray(data).map((item) => asRecord(item));
}

function isMissingRpc(error: SupabaseError): boolean {
  return error.code === 'PGRST202' || /could not find the function/i.test(error.message);
}

function isWriteDenied(error: SupabaseError): boolean {
  return error.code === '42501' || /permission denied|violates row-level security/i.test(error.message);
}

function groupByTenant(rows: Row[]): Map<string, Row[]> {
  const grouped = new Map<string, Row[]>();
  for (const row of rows) {
    const tenantId = readString(row.tenant_id);
    if (!tenantId) continue;
    const existing = grouped.get(tenantId);
    if (existing) existing.push(row);
    else grouped.set(tenantId, [row]);
  }
  return grouped;
}

function firstByTenant(rows: Row[]): Map<string, Row> {
  const grouped = new Map<string, Row>();
  for (const row of rows) {
    const tenantId = readString(row.tenant_id);
    if (tenantId && !grouped.has(tenantId)) grouped.set(tenantId, row);
  }
  return grouped;
}

function sumRows(rows: Row[], field: string): number {
  return rows.reduce((total, row) => total + readNumber(row[field]), 0);
}

function latestDate(values: Array<string | null>): string | null {
  let best: string | null = null;
  let bestTime = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (!value) continue;
    const time = Date.parse(value);
    if (Number.isFinite(time) && time > bestTime) {
      best = value;
      bestTime = time;
    }
  }
  return best;
}

function isWithinDays(value: string | null, days: number): boolean {
  if (!value) return false;
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return false;
  return time >= Date.now() - days * 24 * 60 * 60 * 1000;
}

function buildTotals(tenants: PlatformAdminTenant[]): PlatformAdminTotals {
  return tenants.reduce<PlatformAdminTotals>(
    (totals, tenant) => ({
      tenants: totals.tenants + 1,
      activeTenants: totals.activeTenants + (tenant.isActive ? 1 : 0),
      suspendedTenants: totals.suspendedTenants + (tenant.isActive ? 0 : 1),
      trialingTenants: totals.trialingTenants + (tenant.subscriptionStatus === 'trialing' ? 1 : 0),
      paidTenants: totals.paidTenants + (tenant.subscriptionStatus === 'active' ? 1 : 0),
      pastDueTenants: totals.pastDueTenants + (tenant.subscriptionStatus === 'past_due' ? 1 : 0),
      trialEndingSoon: totals.trialEndingSoon + (isTrialEndingSoon(tenant.trialEndsAt) ? 1 : 0),
      teamMembers: totals.teamMembers + tenant.teamMembers,
      clients: totals.clients + tenant.clients,
      articles: totals.articles + tenant.articles,
      salesDocuments: totals.salesDocuments + tenant.salesDocuments,
      invoiceRevenue: totals.invoiceRevenue + tenant.invoiceRevenue,
      onlineRevenue: totals.onlineRevenue + tenant.onlineRevenue,
      onlineOrders: totals.onlineOrders + tenant.onlineOrders,
      onlineCustomers: totals.onlineCustomers + tenant.onlineCustomers,
      publishedProducts: totals.publishedProducts + tenant.publishedProducts,
      openAbandonedCarts: totals.openAbandonedCarts + tenant.openAbandonedCarts,
      abandonedCartValue: totals.abandonedCartValue + tenant.abandonedCartValue,
      proCustomers: totals.proCustomers + tenant.proCustomers,
      pendingProCustomers: totals.pendingProCustomers + tenant.pendingProCustomers,
      proOrders: totals.proOrders + tenant.proOrders,
      proOrderTotal: totals.proOrderTotal + tenant.proOrderTotal,
      storageBytes: totals.storageBytes + tenant.storageBytes,
      storageFiles: totals.storageFiles + tenant.storageFiles,
      storefrontVisits: totals.storefrontVisits + tenant.storefrontVisits,
      storefrontUniqueVisitors: totals.storefrontUniqueVisitors + tenant.storefrontUniqueVisitors,
      storefrontVisits30d: totals.storefrontVisits30d + tenant.storefrontVisits30d,
      storefrontUniqueVisitors30d:
        totals.storefrontUniqueVisitors30d + tenant.storefrontUniqueVisitors30d,
      unreadNotifications: totals.unreadNotifications + tenant.unreadNotifications,
      revenue30d: totals.revenue30d + tenant.revenue30d,
    }),
    {
      tenants: 0,
      activeTenants: 0,
      suspendedTenants: 0,
      trialingTenants: 0,
      paidTenants: 0,
      pastDueTenants: 0,
      trialEndingSoon: 0,
      teamMembers: 0,
      clients: 0,
      articles: 0,
      salesDocuments: 0,
      invoiceRevenue: 0,
      onlineRevenue: 0,
      onlineOrders: 0,
      onlineCustomers: 0,
      publishedProducts: 0,
      openAbandonedCarts: 0,
      abandonedCartValue: 0,
      proCustomers: 0,
      pendingProCustomers: 0,
      proOrders: 0,
      proOrderTotal: 0,
      storageBytes: 0,
      storageFiles: 0,
      storefrontVisits: 0,
      storefrontUniqueVisitors: 0,
      storefrontVisits30d: 0,
      storefrontUniqueVisitors30d: 0,
      unreadNotifications: 0,
      revenue30d: 0,
    },
  );
}

function isTrialEndingSoon(value: string | null): boolean {
  if (!value) return false;
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return false;
  const now = Date.now();
  return time >= now && time <= now + 7 * 24 * 60 * 60 * 1000;
}

function buildMonthlyRevenue(rows: Row[]): PlatformAdminMonthlyRevenue[] {
  const byMonth = new Map<string, PlatformAdminMonthlyRevenue>();
  for (const row of rows) {
    const date = readString(row.doc_date);
    const month = date?.slice(0, 7);
    if (!month) continue;
    const current = byMonth.get(month) ?? {
      month,
      invoiceRevenue: 0,
      onlineRevenue: 0,
      onlineOrders: 0,
    };
    if (readString(row.doc_type) === 'invoice') {
      current.invoiceRevenue += readNumber(row.total_ttc);
    }
    if (readString(row.doc_type) === 'order' && readString(row.channel) === 'storefront') {
      current.onlineRevenue += readNumber(row.total_ttc);
      current.onlineOrders += 1;
    }
    byMonth.set(month, current);
  }
  return Array.from(byMonth.values())
    .sort((a, b) => a.month.localeCompare(b.month))
    .slice(-12);
}

function nullIfBlank(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function mapDashboard(row: Record<string, unknown>, adminRpc = false): PlatformAdminDashboard {
  const features = asRecord(row.features);
  return {
    generatedAt: readString(row.generatedAt) ?? new Date().toISOString(),
    features: {
      adminRpc: readNullableBoolean(features.adminRpc) ?? adminRpc,
      subscriptionStatusWrite:
        readNullableBoolean(features.subscriptionStatusWrite) ?? adminRpc,
    },
    totals: mapTotals(asRecord(row.totals)),
    landing: mapLandingUsage(asRecord(row.landing)),
    tenants: readArray(row.tenants).map((item) => mapTenant(asRecord(item))),
    monthlyRevenue: readArray(row.monthlyRevenue).map((item) =>
      mapMonthlyRevenue(asRecord(item)),
    ),
  };
}

function mapTenantViewHandoff(row: Record<string, unknown>): PlatformAdminTenantViewHandoff {
  return {
    token: readString(row.token) ?? '',
    ...mapTenantViewAccess(row),
  };
}

function mapTenantViewAccess(row: Record<string, unknown>): PlatformAdminTenantViewAccess {
  return {
    tenantId: readString(row.tenantId) ?? '',
    expiresAt: readString(row.expiresAt) ?? '',
    viewExpiresAt: readString(row.viewExpiresAt) ?? '',
  };
}

function mapTotals(row: Record<string, unknown>): PlatformAdminTotals {
  return {
    tenants: readNumber(row.tenants),
    activeTenants: readNumber(row.activeTenants),
    suspendedTenants: readNumber(row.suspendedTenants),
    trialingTenants: readNumber(row.trialingTenants),
    paidTenants: readNumber(row.paidTenants),
    pastDueTenants: readNumber(row.pastDueTenants),
    trialEndingSoon: readNumber(row.trialEndingSoon),
    teamMembers: readNumber(row.teamMembers),
    clients: readNumber(row.clients),
    articles: readNumber(row.articles),
    salesDocuments: readNumber(row.salesDocuments),
    invoiceRevenue: readNumber(row.invoiceRevenue),
    onlineRevenue: readNumber(row.onlineRevenue),
    onlineOrders: readNumber(row.onlineOrders),
    onlineCustomers: readNumber(row.onlineCustomers),
    publishedProducts: readNumber(row.publishedProducts),
    openAbandonedCarts: readNumber(row.openAbandonedCarts),
    abandonedCartValue: readNumber(row.abandonedCartValue),
    proCustomers: readNumber(row.proCustomers),
    pendingProCustomers: readNumber(row.pendingProCustomers),
    proOrders: readNumber(row.proOrders),
    proOrderTotal: readNumber(row.proOrderTotal),
    storageBytes: readNumber(row.storageBytes),
    storageFiles: readNumber(row.storageFiles),
    storefrontVisits: readNumber(row.storefrontVisits),
    storefrontUniqueVisitors: readNumber(row.storefrontUniqueVisitors),
    storefrontVisits30d: readNumber(row.storefrontVisits30d),
    storefrontUniqueVisitors30d: readNumber(row.storefrontUniqueVisitors30d),
    unreadNotifications: readNumber(row.unreadNotifications),
    revenue30d: readNumber(row.revenue30d),
  };
}

function mapTenantUsage(row: Record<string, unknown>): Pick<
  PlatformAdminTenant,
  | 'id'
  | 'storageBytes'
  | 'storageFiles'
  | 'storageBuckets'
  | 'storefrontVisits'
  | 'storefrontUniqueVisitors'
  | 'storefrontVisits30d'
  | 'storefrontUniqueVisitors30d'
> & { tenantId: string } {
  return {
    id: readString(row.tenantId) ?? '',
    tenantId: readString(row.tenantId) ?? '',
    storageBytes: readNumber(row.storageBytes),
    storageFiles: readNumber(row.storageFiles),
    storageBuckets: readArray(row.storageBuckets)
      .map((item) => {
        const bucket = asRecord(item);
        return {
          bucket: readString(bucket.bucket) ?? '',
          files: readNumber(bucket.files),
          bytes: readNumber(bucket.bytes),
        };
      })
      .filter((bucket) => bucket.bucket),
    storefrontVisits: readNumber(row.storefrontVisits),
    storefrontUniqueVisitors: readNumber(row.storefrontUniqueVisitors),
    storefrontVisits30d: readNumber(row.storefrontVisits30d),
    storefrontUniqueVisitors30d: readNumber(row.storefrontUniqueVisitors30d),
  };
}

function mapLandingUsage(row: Record<string, unknown>): PlatformAdminLandingUsage {
  return {
    visits: readNumber(row.visits),
    uniqueVisitors: readNumber(row.uniqueVisitors),
    visits30d: readNumber(row.visits30d),
    uniqueVisitors30d: readNumber(row.uniqueVisitors30d),
    topPaths30d: readArray(row.topPaths30d)
      .map((item) => {
        const path = asRecord(item);
        return {
          path: readString(path.path) ?? '/',
          visits: readNumber(path.visits),
          uniqueVisitors: readNumber(path.uniqueVisitors),
        };
      })
      .filter((item) => item.path),
  };
}

function mapTenant(row: Record<string, unknown>): PlatformAdminTenant {
  return {
    id: readString(row.id) ?? '',
    name: readString(row.name) ?? '',
    subdomain: readString(row.subdomain) ?? '',
    customDomain: readString(row.customDomain),
    mf: readString(row.mf),
    phone: readString(row.phone),
    email: readString(row.email),
    ownerUserId: readString(row.ownerUserId) ?? '',
    ownerEmail: readString(row.ownerEmail),
    plan: readString(row.plan) ?? 'free',
    isActive: readBoolean(row.isActive),
    createdAt: readString(row.createdAt) ?? '',
    updatedAt: readString(row.updatedAt) ?? '',
    companyName: readString(row.companyName),
    storeName: readString(row.storeName),
    logoUrl: readString(row.logoUrl),
    primaryColor: readString(row.primaryColor),
    defaultLanguage: readString(row.defaultLanguage),
    currency: readString(row.currency),
    whatsappEnabled: readNullableBoolean(row.whatsappEnabled),
    storeCustomDomain: readString(row.storeCustomDomain),
    subscriptionStatus: readString(row.subscriptionStatus) as PlatformAdminTenant['subscriptionStatus'],
    billingCycle: readString(row.billingCycle),
    trialEndsAt: readString(row.trialEndsAt),
    currentPeriodEnd: readString(row.currentPeriodEnd),
    canceledAt: readString(row.canceledAt),
    teamMembers: readNumber(row.teamMembers),
    enabledModules: readNumber(row.enabledModules),
    modules: readArray(row.modules).map((item) => mapModule(asRecord(item))),
    salesDocuments: readNumber(row.salesDocuments),
    salesOrders: readNumber(row.salesOrders),
    invoices: readNumber(row.invoices),
    invoiceRevenue: readNumber(row.invoiceRevenue),
    onlineOrders: readNumber(row.onlineOrders),
    onlineRevenue: readNumber(row.onlineRevenue),
    revenue30d: readNumber(row.revenue30d),
    purchaseDocuments: readNumber(row.purchaseDocuments),
    purchaseInvoiceTotal: readNumber(row.purchaseInvoiceTotal),
    clients: readNumber(row.clients),
    suppliers: readNumber(row.suppliers),
    articles: readNumber(row.articles),
    payments: readNumber(row.payments),
    paymentTotal: readNumber(row.paymentTotal),
    storefrontProducts: readNumber(row.storefrontProducts),
    publishedProducts: readNumber(row.publishedProducts),
    onlineCustomers: readNumber(row.onlineCustomers),
    onlineCustomerLtv: readNumber(row.onlineCustomerLtv),
    openAbandonedCarts: readNumber(row.openAbandonedCarts),
    abandonedCartValue: readNumber(row.abandonedCartValue),
    proCustomers: readNumber(row.proCustomers),
    pendingProCustomers: readNumber(row.pendingProCustomers),
    approvedProCustomers: readNumber(row.approvedProCustomers),
    proOrders: readNumber(row.proOrders),
    proOrderTotal: readNumber(row.proOrderTotal),
    storageBytes: readNumber(row.storageBytes),
    storageFiles: readNumber(row.storageFiles),
    storageBuckets: readArray(row.storageBuckets)
      .map((item) => {
        const bucket = asRecord(item);
        return {
          bucket: readString(bucket.bucket) ?? '',
          files: readNumber(bucket.files),
          bytes: readNumber(bucket.bytes),
        };
      })
      .filter((bucket) => bucket.bucket),
    storefrontVisits: readNumber(row.storefrontVisits),
    storefrontUniqueVisitors: readNumber(row.storefrontUniqueVisitors),
    storefrontVisits30d: readNumber(row.storefrontVisits30d),
    storefrontUniqueVisitors30d: readNumber(row.storefrontUniqueVisitors30d),
    unreadNotifications: readNumber(row.unreadNotifications),
    lastActivityAt: readString(row.lastActivityAt),
  };
}

function mapModule(row: Record<string, unknown>): PlatformAdminModuleState {
  return {
    key: readString(row.key) ?? '',
    enabled: readBoolean(row.enabled),
  };
}

function mapMonthlyRevenue(row: Record<string, unknown>): PlatformAdminMonthlyRevenue {
  return {
    month: readString(row.month) ?? '',
    invoiceRevenue: readNumber(row.invoiceRevenue),
    onlineRevenue: readNumber(row.onlineRevenue),
    onlineOrders: readNumber(row.onlineOrders),
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function readBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  return false;
}

function readNullableBoolean(value: unknown): boolean | null {
  if (value == null) return null;
  return readBoolean(value);
}
